import React, { useEffect, useState } from 'react';
import { X, Pencil, Save, FileText } from 'lucide-react';
import { Task } from '../lib/services/tasksService';
import { format, parseISO } from 'date-fns';
import { fr } from 'date-fns/locale';

interface TaskDescriptionModalProps {
  task: Task | null;
  isOpen: boolean;
  onClose: () => void;
  onSaveDescription: (taskId: string, description: string | null) => Promise<{ success: boolean; error?: string }>;
}

const TaskDescriptionModal: React.FC<TaskDescriptionModalProps> = ({
  task,
  isOpen,
  onClose,
  onSaveDescription,
}) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen || !task) return;
    setDraft(task.description ?? '');
    setEditing(false);
    setError(null);
  }, [isOpen, task]);

  useEffect(() => {
    if (isOpen) document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  if (!isOpen || !task) return null;

  const handleSave = async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await onSaveDescription(task.id, draft.trim() || null);
      if (result.success) {
        setEditing(false);
      } else {
        setError(result.error ?? 'Erreur lors de l’enregistrement');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur inattendue');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (loading) return;
    setEditing(false);
    onClose();
  };

  return (
    <div
      className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="task-description-title"
      onClick={handleClose}
    >
      <div
        className="bg-dark-800 border border-dark-700 rounded-xl shadow-xl w-full max-w-3xl max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3 p-5 sm:p-6 border-b border-dark-700 shrink-0">
          <div className="min-w-0">
            <div className="flex items-center gap-2 text-dark-400 text-xs mb-1">
              <FileText className="w-3.5 h-3.5 shrink-0" />
              <span>Description de la tâche</span>
            </div>
            <h2
              id="task-description-title"
              className={`text-xl sm:text-2xl font-semibold text-white leading-snug ${
                task.completed ? 'line-through text-dark-400' : ''
              }`}
            >
              {task.title}
            </h2>
            <p className="text-sm text-dark-500 mt-2">
              Échéance : {format(parseISO(task.due_date), 'd MMMM yyyy', { locale: fr })}
            </p>
          </div>
          <div className="flex items-center gap-1 shrink-0">
            {!editing && (
              <button
                type="button"
                onClick={() => setEditing(true)}
                className="inline-flex items-center gap-1.5 px-3 py-2 text-sm rounded-lg bg-dark-700 text-white hover:bg-dark-600 focus:outline-none focus:ring-2 focus:ring-primary-500/50"
              >
                <Pencil className="w-4 h-4" />
                Modifier
              </button>
            )}
            <button
              type="button"
              onClick={handleClose}
              className="p-2 text-dark-400 hover:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500/50"
              aria-label="Fermer"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-5 sm:p-6">
          {editing ? (
            <textarea
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              className="w-full min-h-[50vh] px-4 py-3 bg-dark-700 border border-dark-600 rounded-lg text-white placeholder-dark-500 focus:outline-none focus:ring-2 focus:ring-primary-500/50 focus:border-transparent resize-y text-base leading-relaxed"
              placeholder="Rédigez la description… (aussi longue qu’un article)"
              disabled={loading}
              autoFocus
            />
          ) : task.description?.trim() ? (
            <article className="prose prose-invert max-w-none">
              <div className="text-dark-200 text-base sm:text-[17px] leading-7 whitespace-pre-wrap break-words">
                {task.description}
              </div>
            </article>
          ) : (
            <p className="text-dark-500 text-sm italic">Aucune description pour cette tâche.</p>
          )}
        </div>

        {(editing || error) && (
          <div className="shrink-0 border-t border-dark-700 p-4 sm:p-5 space-y-3">
            {error && (
              <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/20 text-red-400 text-sm">
                {error}
              </div>
            )}
            {editing && (
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => {
                    setDraft(task.description ?? '');
                    setEditing(false);
                    setError(null);
                  }}
                  disabled={loading}
                  className="flex-1 px-4 py-3 rounded-lg bg-dark-700 text-white font-medium hover:bg-dark-600 transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500/50 disabled:opacity-50"
                >
                  Annuler
                </button>
                <button
                  type="button"
                  onClick={handleSave}
                  disabled={loading}
                  className="flex-1 px-4 py-3 rounded-lg bg-primary-500 text-white font-medium hover:bg-primary-600 transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500/50 disabled:opacity-50 inline-flex items-center justify-center gap-2"
                >
                  {loading ? (
                    <span className="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <Save className="w-4 h-4" />
                  )}
                  <span>{loading ? 'Enregistrement…' : 'Enregistrer'}</span>
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default TaskDescriptionModal;
